import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import uuid from 'uuid/v4';
import get from 'lodash/get';
import { Icon } from '@els/els-react--icon';
import { Card } from '@els/els-react--card';
import { Button } from '@els/els-react--button';
import { TextInput } from '@els/els-react--text-input';
import { RadioButtonGroup } from '@els/els-react--radio-button';
import Catalyst from '@Services/Catalyst';
import AppStorage from '@Services/AppStorage';
import UserDatabases from '@Services/UserDatabases';
import IntegratedFeedbackService from '@Services/IntegratedFeedbackService';
import Auth from '@Services/Auth';
import { FEATURES } from '@Constants/rx-ui/constants';

import './IntegratedFeedback.scss';

// Prevent warning from Leyden
RadioButtonGroup.Option.propTypes.label = PropTypes.oneOfType([PropTypes.string, PropTypes.node]);

function IntegratedFeedback(props) {
  const [rating, setRating] = useState(-1);
  const [ratingFeedback, setRatingFeedback] = useState(null);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [isFeedbackExpanded, setIsFeedbackExpanded] = useState(false);
  const { userSearchInput, resultState, searchType, showSuccessToastMessage, onHide } = props;
  const extendedOnNegativeFeedbackOnly = Auth.isFeatureEnabled(FEATURES.extendedOnNegativeFeedbackOnly);

  useEffect(() => {
    if (get(resultState, 'pageNumber') > 1) {
      // set to hide component
      AppStorage.instance.setToSessionStorage('integratedFeedbackHide', true);
    }
  }, [resultState]);

  useEffect(() => {
    Catalyst.trackIntegratedFeedbackRendered();
  }, []);

  function getSprite(ratingLevel) {
    let sprite;

    switch (ratingLevel) {
      case 1:
        sprite = {
          icon: 'FeedbackSad',
          a11yName: 'Unsatisfied',
        };
        break;
      case 2:
        sprite = {
          icon: 'FeedbackMeh',
          a11yName: 'Neutral',
        };
        break;
      case 3:
        sprite = {
          icon: 'FeedbackHappy',
          a11yName: 'Satisfied',
        };
        break;
      case 4:
        sprite = {
          icon: 'FeedbackExcited',
          a11yName: 'Very satisfied',
        };
        break;
      default:
        throw new Error(`Received an unexpected rating level: ${ratingLevel}`);
    }

    return sprite;
  }

  function isShowIntegratedFeedback() {
    const hideIntegratedFeedback = !!AppStorage.instance.getFromSessionStorage('integratedFeedbackHide');
    const isCI = !!UserDatabases.isCIDatabase(resultState.databaseId);

    return searchType === 'quick' && !hideIntegratedFeedback && !isCI;
  }

  // set to hide component
  function hideFeedback() {
    AppStorage.instance.setToSessionStorage('integratedFeedbackHide', true);
    onHide(true);
  }

  function closeFeedback() {
    Catalyst.trackIntegratedFeedbackClosed();
    hideFeedback();
  }

  function submitFeedback(score, verbatimFeedback) {
    Catalyst.trackIntegratedSearchFeedbackClick(score, verbatimFeedback, selectedFeedback);
    const { query, structure } = userSearchInput;
    const requestBody = {
      rating: score,
      feedback: verbatimFeedback,
      feedbackChoice: selectedFeedback,
      userSearchInput: query && query.trim() !== '' ? query : structure,
      context: resultState.context,
      transformedSearch: resultState.description,
    };
    Logger.info('Submitting feedback to Backend', requestBody);

    try {
      IntegratedFeedbackService.createIntegratedFeedback(requestBody);
    } catch (e) {
      Logger.error('Failed to post Integrated Feedback', e);
    }

    setIsFeedbackExpanded(false);
    showSuccessToastMessage({ content: 'Thank you. Your feedback will be used to help improve the quality of the experience we provide.' });

    hideFeedback();
  }

  function ratingClicked(ratingLevel) {
    Catalyst.trackIntegratedSearchClick(ratingLevel);
    setRating(ratingLevel);

    if (extendedOnNegativeFeedbackOnly && ratingLevel > 2) {
      submitFeedback(ratingLevel);
    } else {
      setIsFeedbackExpanded(true);
      Catalyst.trackIntegratedFeedbackStep2();
    }
  }

  function getRatingIcon(ratingLevel, disabled) {
    const sprite = getSprite(ratingLevel);
    return (
      <Icon
        sprite={sprite.icon}
        size="m"
        color={rating === ratingLevel ? 'primary' : 'secondary'}
        a11y={{ name: sprite.a11yName }}
        id={sprite.icon + uuid()}
        onClick={() => !disabled && ratingClicked(ratingLevel)}
        className={classnames('u-els-margin-1o2 if-icon', {
          'icon-disabled': !!disabled,
        })}
      />
    );
  }

  const options = [
    {
      key: 'TOO_GENERAL',
      label: 'Were too general/simple',
    },
    {
      key: 'MISSED_CONCEPTS',
      label: 'Missed key concepts',
    },
    {
      key: 'UNRELATED_INFO',
      label: 'Contained unrelated information to the search',
    },
    {
      key: 'HARD_TO_UNDERSTAND',
      label: 'Were hard to understand',
    },
    {
      key: 'FACTUALLY_INCORRECT',
      label: 'Were factually incorrect',
    },
  ];

  function getFeedbackRadios() {
    return options.map((option) => (
      <RadioButtonGroup.Option
        id={option.key + uuid()}
        key={option.key}
        className="reaxys-online-option u-els-margin-bottom-1x"
        title={`Select ${option.label}`}
        value={option.key}
        label={option.label}
      />
    ));
  }

  const onTextInputChange = (e) => {
    if (selectedFeedback !== 'OTHER') {
      setSelectedFeedback('OTHER');
    }
    setRatingFeedback(e.target.value);
  };

  return isShowIntegratedFeedback() ? (
    <Card borderWeight="medium" className="u-els-margin-bottom u-els-margin-top u-els-margin-left-2x u-els-margin-right-2x int-feedback">
      <p className="u-els-margin-bottom-none u-els-margin-right-1x u-els-width-auto u-els-font-size-base">
        How satisfied are you with the result set?
      </p>
      {rating === -1 && (
        <>
          <ol className="u-els-margin-right-1x">
            <li>{getRatingIcon(1)}</li>
            <li>{getRatingIcon(2)}</li>
            <li>{getRatingIcon(3)}</li>
            <li>{getRatingIcon(4)}</li>
          </ol>

          <Icon
            sprite="Close"
            size="s"
            a11y={{ name: 'close search rating' }}
            id={`close_${uuid()}`}
            onClick={() => closeFeedback()}
            className="u-els-margin-1o2 u-els-margin-top-none if-icon close-card"
          />
        </>
      )}
      {rating !== -1 && <div className="selected-rating">{getRatingIcon(rating, true)}</div>}

      {isFeedbackExpanded && (
        <>
          <p className="u-els-font-family-bold u-els-font-size-base u-els-display-block u-els-width-auto">
            I chose this rating because the results:
          </p>
          <RadioButtonGroup
            value={selectedFeedback}
            shouldBeSelectedByDefault={false}
            onChange={(event) => {
              const { value } = event.currentTarget;
              setSelectedFeedback(value);
            }}
            a11y={{ name: 'Select an option' }}
            shouldHideLabel>
            {getFeedbackRadios()}
            <RadioButtonGroup.Option
              id={`other${uuid()}`}
              key="OTHER"
              title="Select other"
              className="reaxys-online-option other-radio-flex-container u-els-display-flex"
              value="OTHER"
              label={
                <TextInput
                  label="Other"
                  className="u-els-width-1o1"
                  size="small"
                  shouldHideLabel
                  value={ratingFeedback}
                  onChange={onTextInputChange}
                />
              }
            />
          </RadioButtonGroup>
          <Button
            className="u-els-display-block u-els-margin-right-auto u-els-margin-left-none u-els-margin-top-1x"
            onClick={() => submitFeedback(rating, ratingFeedback)}>
            Submit
          </Button>
        </>
      )}
    </Card>
  ) : null;
}

IntegratedFeedback.propTypes = {
  searchType: PropTypes.string,
  userSearchInput: PropTypes.object,
  resultState: PropTypes.object,
  showSuccessToastMessage: PropTypes.func,
  onHide: PropTypes.func,
};

export default IntegratedFeedback;
